import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux'
import styled, { css } from 'styled-components';
import { doA11yValidation } from 'zengenti-forms-package/utils/validate-utils';
import Label from '../label/label';
import { makeSelectFormValidationSent } from 'zengenti-forms-package/redux/selectors';
import { CharacterLimit } from 'zengenti-forms-package/components/charaterlimit'
import MarkdownRenderer from 'zengenti-forms-package/utils/markdown-to-jsx';

const TextfieldStyled = styled.div`
  ${({ isHidden }) => {
    return css`
      display: ${isHidden ? 'none' : 'block'};
      position: relative;
    `;
  }};
`;

const Textfield = ({
  className,
  field,
  formId,
  id,
  label,
  type,
  setValue,
  onValidateField,
  validations,
  defaultValue,
  defaultLanguage,
  placeholder,
  isHidden,
  useDefaultTheme,
  instructions,
}) => {
  const [hasCharLimit, setCharLimit] = useState(false);
  const isRequired = validations && validations.required ? true : false;
  const placeholderText =
    placeholder &&
    placeholder.properties &&
    placeholder.properties.placeholderText &&
    placeholder.properties.placeholderText[defaultLanguage];


  const defaultValueText =
    defaultValue && defaultValue[defaultLanguage]
      ? defaultValue[defaultLanguage]
      : defaultValue;
  
      
  const [a11yInvalid, setA11yInvalid] = useState('');
  const [_, setTextfieldValue] = useState(defaultValueText || ''); 
  const selectFormValidationSent = makeSelectFormValidationSent(formId);
  const formValidationSent = useSelector(selectFormValidationSent);
  useEffect(() => {
    if (formValidationSent) setA11yInvalid(doA11yValidation(_, field, true));
  }, [formValidationSent])

  const [isVisible, setVisible] = React.useState(false);
  
  return (
    <TextfieldStyled className={className} isHidden={isHidden}>
      <Label
        id={id}
        label={label}
        isRequired={isRequired}
        className="text-field__label"
      />
      {hasCharLimit && (
        <CharacterLimit
          value={defaultValue}
          validations={validations}
          defaultLanguage={defaultLanguage}
          useDefaultTheme={useDefaultTheme}
        />
      )}
      <div className="text-field__input--wrapper" style={{ position: 'relative'}}>
        <input
          className='text-field__input'
          type={(isVisible ?? 'text') || type}
          defaultValue={defaultValueText}
          placeholder={placeholderText}
          aria-invalid={a11yInvalid}
          id={id}
          name={id}
          onChange={e => {
            setTextfieldValue(e.target.value);
            setValue(formId, id, e.target.value);
          }}
          onBlur={e => {
            onValidateField(formId, id, e.target.value);
            setCharLimit(false);
            setA11yInvalid(doA11yValidation(e.target.value, field, formValidationSent))
          }}
          onFocus={() => {
            setCharLimit(true);
          }}
        />
        {a11yInvalid == 'true' && (
          <svg className="svg" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" role="presentation" width="16" height="16" fill="none"><path fill="#333" d="M12.2 3.807a.664.664 0 0 0-.94 0L8 7.06 4.74 3.8a.665.665 0 0 0-.94.94L7.06 8 3.8 11.26a.664.664 0 1 0 .94.94L8 8.94l3.26 3.26a.665.665 0 0 0 .94-.94L8.94 8l3.26-3.26a.668.668 0 0 0 0-.933Z"/></svg>
        )}
        {a11yInvalid == 'false' && (
          <svg className="svg" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true" role="presentation" width="16" height="16" fill="none"><path fill="#333" d="m2 8 4.418 4.667L14 4.659l-1.246-1.326-6.336 6.692-3.18-3.332L2 8Z"/></svg>
        )}
      </div>
      {instructions && <MarkdownRenderer className="text-field__input--markdown" markdown={instructions} />}
      {id === 'password' && (
        <button className="text-input__button--pw" type="button" onClick={() => setVisible(!isVisible)} aria-label={`${isVisible ? 'Hide' : 'Show'} Password.`}>
          {isVisible ? (
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3536 2.35355C13.5488 2.15829 13.5488 1.84171 13.3536 1.64645C13.1583 1.45118 12.8417 1.45118 12.6464 1.64645L10.6828 3.61012C9.70652 3.21671 8.63759 3 7.5 3C4.30786 3 1.65639 4.70638 0.0760002 7.23501C-0.0253338 7.39715 -0.0253334 7.60288 0.0760014 7.76501C0.902945 9.08812 2.02314 10.1861 3.36061 10.9323L1.64645 12.6464C1.45118 12.8417 1.45118 13.1583 1.64645 13.3536C1.84171 13.5488 2.15829 13.5488 2.35355 13.3536L4.31723 11.3899C5.29348 11.7833 6.36241 12 7.5 12C10.6921 12 13.3436 10.2936 14.924 7.76501C15.0253 7.60288 15.0253 7.39715 14.924 7.23501C14.0971 5.9119 12.9769 4.81391 11.6394 4.06771L13.3536 2.35355ZM9.90428 4.38861C9.15332 4.1361 8.34759 4 7.5 4C4.80285 4 2.52952 5.37816 1.09622 7.50001C1.87284 8.6497 2.89609 9.58106 4.09974 10.1931L9.90428 4.38861ZM5.09572 10.6114L10.9003 4.80685C12.1039 5.41894 13.1272 6.35031 13.9038 7.50001C12.4705 9.62183 10.1971 11 7.5 11C6.65241 11 5.84668 10.8639 5.09572 10.6114Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg> 
          ) : (              
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 11C4.80285 11 2.52952 9.62184 1.09622 7.50001C2.52952 5.37816 4.80285 4 7.5 4C10.1971 4 12.4705 5.37816 13.9038 7.50001C12.4705 9.62183 10.1971 11 7.5 11ZM7.5 3C4.30786 3 1.65639 4.70638 0.0760002 7.23501C-0.0253338 7.39715 -0.0253334 7.60288 0.0760014 7.76501C1.65639 10.2936 4.30786 12 7.5 12C10.6921 12 13.3436 10.2936 14.924 7.76501C15.0253 7.60288 15.0253 7.39715 14.924 7.23501C13.3436 4.70638 10.6921 3 7.5 3ZM7.5 9.5C8.60457 9.5 9.5 8.60457 9.5 7.5C9.5 6.39543 8.60457 5.5 7.5 5.5C6.39543 5.5 5.5 6.39543 5.5 7.5C5.5 8.60457 6.39543 9.5 7.5 9.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
          )}
        </button>
      )}
    </TextfieldStyled>
  );
};

Textfield.propTypes = {
  className: PropTypes.string,
  field: PropTypes.any,
  formId: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  setValue: PropTypes.func,
  onValidateField: PropTypes.func,
  validations: PropTypes.object,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  defaultLanguage: PropTypes.string,
  placeholder: PropTypes.object,
  isHidden: PropTypes.bool,
  errors: PropTypes.array,
  useDefaultTheme: PropTypes.bool,
};

export default Textfield;
